import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle, CheckCircle, Copy, Key, MoreHorizontal,
  Plus, RefreshCw, Shield, ShieldOff, UserCheck, Users, X,
} from 'lucide-react';
import { Button, Card, CardHeader, CardTitle, EmptyState, PageSpinner, TBody, TD, TH, THead, TR, Table } from '@/components/ui';
import { api } from '@/lib/api/client';
import { isApiError, unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';
import { cn } from '@/lib/utils/cn';

// ── Types ────────────────────────────────────────────────────────────────────

type ClientRole =
  | 'AUTHMATECH_CLIENT_ADMIN'
  | 'AUTHMATECH_CLIENT_OPERATOR'
  | 'AUTHMATECH_CLIENT_FINANCE'
  | 'AUTHMATECH_CLIENT_VIEWER';

type MemberStatus = 'ACTIVE' | 'SUSPENDED';

interface TeamMember {
  id: string;
  keycloakUserId: string | null;
  email: string;
  fullName: string;
  role: ClientRole;
  status: MemberStatus;
  createdBy: string | null;
  createdAt: string | null;
}

interface InviteResult {
  email: string;
  fullName: string;
  role: ClientRole;
  temporaryPassword: string | null;
  keycloakProvisioned: boolean;
  showCredentialsModal: boolean;
  smtpNote: string | null;
}

interface InviteForm {
  email: string;
  fullName: string;
  role: ClientRole;
}

// ── Role helpers ─────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<ClientRole, string> = {
  AUTHMATECH_CLIENT_ADMIN: 'Admin',
  AUTHMATECH_CLIENT_OPERATOR: 'Operator',
  AUTHMATECH_CLIENT_FINANCE: 'Finance',
  AUTHMATECH_CLIENT_VIEWER: 'Viewer',
};

const ROLE_OPTIONS: { value: ClientRole; label: string }[] = [
  { value: 'AUTHMATECH_CLIENT_ADMIN', label: 'Admin' },
  { value: 'AUTHMATECH_CLIENT_OPERATOR', label: 'Operator' },
  { value: 'AUTHMATECH_CLIENT_FINANCE', label: 'Finance' },
  { value: 'AUTHMATECH_CLIENT_VIEWER', label: 'Viewer' },
];

const ROLE_MATRIX: { action: string; admin: boolean; operator: boolean; finance: boolean; viewer: boolean }[] = [
  { action: 'View opportunities & verifications', admin: true, operator: true, finance: true, viewer: true },
  { action: 'View usage analytics', admin: true, operator: true, finance: true, viewer: true },
  { action: 'View subscription & balance', admin: true, operator: false, finance: true, viewer: false },
  { action: 'Submit support requests', admin: true, operator: true, finance: true, viewer: false },
  { action: 'Manage team members', admin: true, operator: false, finance: false, viewer: false },
  { action: 'Reset member passwords', admin: true, operator: false, finance: false, viewer: false },
  { action: 'Suspend / reactivate members', admin: true, operator: false, finance: false, viewer: false },
];

function RoleBadge({ role }: { role: ClientRole }) {
  const map: Record<ClientRole, string> = {
    AUTHMATECH_CLIENT_ADMIN: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
    AUTHMATECH_CLIENT_OPERATOR: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
    AUTHMATECH_CLIENT_FINANCE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
    AUTHMATECH_CLIENT_VIEWER: 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300',
  };
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', map[role])}>
      {ROLE_LABELS[role]}
    </span>
  );
}

function StatusDot({ status }: { status: MemberStatus }) {
  return (
    <span className="flex items-center gap-1.5 text-xs">
      <span className={cn('h-1.5 w-1.5 rounded-full', status === 'ACTIVE' ? 'bg-emerald-500' : 'bg-amber-500')} />
      {status === 'ACTIVE' ? 'Active' : 'Suspended'}
    </span>
  );
}

// ── API ───────────────────────────────────────────────────────────────────────

async function fetchTeam(): Promise<TeamMember[]> {
  const res = await api.get<ResponseEnvelope<TeamMember[]>>(Endpoints.me.team);
  return unwrap(res);
}

async function inviteMember(body: InviteForm): Promise<InviteResult> {
  const res = await api.post<ResponseEnvelope<InviteResult>>(Endpoints.me.teamInvite, body);
  return unwrap(res);
}

async function changeRole(userId: string, role: ClientRole): Promise<void> {
  const res = await api.patch<ResponseEnvelope<unknown>>(Endpoints.me.teamMemberRole(userId), { role });
  unwrap(res);
}

async function resetPassword(userId: string): Promise<InviteResult> {
  const res = await api.post<ResponseEnvelope<InviteResult>>(Endpoints.me.teamMemberResetPassword(userId));
  return unwrap(res);
}

async function suspendMember(userId: string): Promise<void> {
  const res = await api.post<ResponseEnvelope<unknown>>(Endpoints.me.teamMemberSuspend(userId));
  unwrap(res);
}

async function reactivateMember(userId: string): Promise<void> {
  const res = await api.post<ResponseEnvelope<unknown>>(Endpoints.me.teamMemberReactivate(userId));
  unwrap(res);
}

// ── Credentials modal ─────────────────────────────────────────────────────────

function CredentialsModal({
  result,
  onClose,
}: {
  result: InviteResult;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copyAll() {
    const text = `Email: ${result.email}\nPassword: ${result.temporaryPassword ?? ''}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface shadow-2xl">
        <div className="flex items-start justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-500/15">
              <Key className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-fg">One-Time Credentials</p>
              <p className="text-xs text-fg-muted">Share these securely — shown only once</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-fg-muted hover:bg-surface-muted hover:text-fg">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div className="rounded-xl border border-border bg-surface-muted p-4 space-y-3 font-mono text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-fg-subtle mb-1">Email</p>
              <p className="text-fg font-medium">{result.email}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-fg-subtle mb-1">Temporary Password</p>
              <p className="text-fg font-semibold tracking-wider">{result.temporaryPassword ?? '—'}</p>
            </div>
          </div>

          {result.smtpNote && (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-300">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <p>{result.smtpNote}</p>
            </div>
          )}

          {!result.keycloakProvisioned && (
            <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-300">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <p>Keycloak provisioning is pending. The user account will be created on first login.</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="outline" size="sm" onClick={copyAll}>
            {copied ? (
              <><CheckCircle className="mr-1.5 h-3.5 w-3.5 text-emerald-500" /> Copied!</>
            ) : (
              <><Copy className="mr-1.5 h-3.5 w-3.5" /> Copy credentials</>
            )}
          </Button>
          <Button size="sm" onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}

// ── Invite modal ──────────────────────────────────────────────────────────────

function InviteModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: (result: InviteResult) => void;
}) {
  const [form, setForm] = useState<InviteForm>({
    email: '',
    fullName: '',
    role: 'AUTHMATECH_CLIENT_VIEWER',
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => inviteMember(form),
    onSuccess: (result) => {
      onSuccess(result);
    },
    onError: (err) => {
      setError(isApiError(err) ? err.firstErrorMessage : 'Failed to invite member.');
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface shadow-2xl">
        <div className="flex items-start justify-between border-b border-border px-6 py-4">
          <div>
            <p className="text-sm font-semibold text-fg">Invite Team Member</p>
            <p className="text-xs text-fg-muted">They'll receive one-time credentials to log in</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-fg-muted hover:bg-surface-muted hover:text-fg">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          {error && (
            <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-300">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-xs font-medium text-fg-muted">Email address</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="user@example.com"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-fg-muted">Full name</label>
            <input
              type="text"
              value={form.fullName}
              onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
              placeholder="Jane Smith"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-fg-muted">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as ClientRole }))}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            >
              {ROLE_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            onClick={() => mutation.mutate()}
            disabled={!form.email || !form.fullName || mutation.isPending}
          >
            {mutation.isPending ? 'Inviting…' : 'Send Invite'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Member actions menu ───────────────────────────────────────────────────────

function MemberActions({
  member,
  onCredentials,
}: {
  member: TeamMember;
  onCredentials: (result: InviteResult) => void;
}) {
  const [open, setOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['me', 'team'] });

  const resetMut = useMutation({
    mutationFn: () => resetPassword(member.id),
    onSuccess: (result) => { onCredentials(result); setOpen(false); },
    onError: (err) => setActionError(isApiError(err) ? err.firstErrorMessage : 'Failed.'),
  });

  const suspendMut = useMutation({
    mutationFn: () => suspendMember(member.id),
    onSuccess: () => { invalidate(); setOpen(false); },
    onError: (err) => setActionError(isApiError(err) ? err.firstErrorMessage : 'Failed.'),
  });

  const reactivateMut = useMutation({
    mutationFn: () => reactivateMember(member.id),
    onSuccess: () => { invalidate(); setOpen(false); },
    onError: (err) => setActionError(isApiError(err) ? err.firstErrorMessage : 'Failed.'),
  });

  const roleMut = useMutation({
    mutationFn: (role: ClientRole) => changeRole(member.id, role),
    onSuccess: () => { invalidate(); setRoleOpen(false); setOpen(false); },
    onError: (err) => setActionError(isApiError(err) ? err.firstErrorMessage : 'Failed.'),
  });

  const busy = resetMut.isPending || suspendMut.isPending || reactivateMut.isPending || roleMut.isPending;

  return (
    <div className="relative">
      {actionError && (
        <p className="absolute -top-6 right-0 text-[10px] text-rose-500 whitespace-nowrap">{actionError}</p>
      )}
      <button
        onClick={() => { setOpen((v) => !v); setActionError(null); }}
        disabled={busy}
        className="rounded-lg p-1.5 text-fg-muted transition-colors hover:bg-surface-muted hover:text-fg disabled:opacity-50"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => { setOpen(false); setRoleOpen(false); }} />
          <div className="absolute right-0 top-8 z-40 min-w-[180px] rounded-xl border border-border bg-surface shadow-lg">
            <div className="p-1">
              {/* Reset password */}
              <button
                onClick={() => resetMut.mutate()}
                disabled={busy}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-xs text-fg hover:bg-surface-muted disabled:opacity-50"
              >
                <RefreshCw className="h-3.5 w-3.5 text-fg-muted" />
                Reset password
              </button>

              {/* Change role */}
              <div className="relative">
                <button
                  onClick={() => setRoleOpen((v) => !v)}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-xs text-fg hover:bg-surface-muted"
                >
                  <Shield className="h-3.5 w-3.5 text-fg-muted" />
                  Change role
                </button>
                {roleOpen && (
                  <div className="absolute right-full top-0 mr-1 min-w-[150px] rounded-xl border border-border bg-surface shadow-lg">
                    <div className="p-1">
                      {ROLE_OPTIONS.filter((o) => o.value !== member.role).map(({ value, label }) => (
                        <button
                          key={value}
                          onClick={() => roleMut.mutate(value)}
                          disabled={roleMut.isPending}
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-fg hover:bg-surface-muted disabled:opacity-50"
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="my-1 border-t border-border" />

              {/* Suspend / reactivate */}
              {member.status === 'ACTIVE' ? (
                <button
                  onClick={() => suspendMut.mutate()}
                  disabled={busy}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-xs text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-500/10 disabled:opacity-50"
                >
                  <ShieldOff className="h-3.5 w-3.5" />
                  Suspend access
                </button>
              ) : (
                <button
                  onClick={() => reactivateMut.mutate()}
                  disabled={busy}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-xs text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 disabled:opacity-50"
                >
                  <UserCheck className="h-3.5 w-3.5" />
                  Reactivate access
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Role matrix ───────────────────────────────────────────────────────────────

function RoleMatrix() {
  const Check = () => <CheckCircle className="mx-auto h-4 w-4 text-emerald-500" />;
  const No = () => <span className="mx-auto block h-4 w-4 text-center text-fg-subtle leading-4">—</span>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Role Permissions</CardTitle>
      </CardHeader>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="pb-3 pl-5 pr-4 text-left text-xs font-medium text-fg-muted">Permission</th>
              {['Admin', 'Operator', 'Finance', 'Viewer'].map((r) => (
                <th key={r} className="w-24 pb-3 px-2 text-center text-xs font-medium text-fg-muted">{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROLE_MATRIX.map((row, i) => (
              <tr key={i} className={cn('border-b border-border/50 last:border-0', i % 2 === 0 ? 'bg-surface' : 'bg-surface-muted/30')}>
                <td className="py-2.5 pl-5 pr-4 text-xs text-fg">{row.action}</td>
                <td className="py-2.5 px-2 text-center">{row.admin ? <Check /> : <No />}</td>
                <td className="py-2.5 px-2 text-center">{row.operator ? <Check /> : <No />}</td>
                <td className="py-2.5 px-2 text-center">{row.finance ? <Check /> : <No />}</td>
                <td className="py-2.5 px-2 text-center">{row.viewer ? <Check /> : <No />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ClientTeamPage() {
  const [showInvite, setShowInvite] = useState(false);
  const [credentials, setCredentials] = useState<InviteResult | null>(null);
  const queryClient = useQueryClient();

  const { data: members, isLoading, isError, error } = useQuery({
    queryKey: ['me', 'team'],
    queryFn: fetchTeam,
    staleTime: 30_000,
  });

  function handleInviteSuccess(result: InviteResult) {
    setShowInvite(false);
    setCredentials(result);
    queryClient.invalidateQueries({ queryKey: ['me', 'team'] });
  }

  const active = members?.filter((m) => m.status === 'ACTIVE') ?? [];
  const suspended = members?.filter((m) => m.status === 'SUSPENDED') ?? [];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-fg">Team & Access</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            Manage who has access to your workspace and what they can do.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowInvite(true)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Invite member
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-surface p-4 shadow-card dark:shadow-card-dark sm:p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-500/15">
              <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-fg-muted">Total Members</p>
              <p className="text-xl font-bold text-fg">{members?.length ?? '—'}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-surface p-4 shadow-card dark:shadow-card-dark sm:p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-500/15">
              <UserCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-fg-muted">Active</p>
              <p className="text-xl font-bold text-fg">{active.length}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-surface p-4 shadow-card dark:shadow-card-dark sm:p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-500/15">
              <ShieldOff className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-fg-muted">Suspended</p>
              <p className="text-xl font-bold text-fg">{suspended.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Member table */}
      <Card noPadding>
        <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
          <p className="text-sm font-medium text-fg">Team members</p>
        </div>

        {isLoading ? (
          <div className="py-12"><PageSpinner /></div>
        ) : isError ? (
          <div className="px-5 py-8">
            <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm dark:border-rose-500/25 dark:bg-rose-500/10">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
              <p className="text-rose-700 dark:text-rose-300">
                {isApiError(error) ? error.firstErrorMessage : 'Could not load team members.'}
              </p>
            </div>
          </div>
        ) : members?.length === 0 ? (
          <EmptyState message="No team members yet. Invite someone to get started." />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Member</TH>
                <TH>Role</TH>
                <TH>Status</TH>
                <TH>Added</TH>
                <TH className="w-12" />
              </TR>
            </THead>
            <TBody>
              {members?.map((member) => (
                <TR key={member.id} className={member.status === 'SUSPENDED' ? 'opacity-60' : ''}>
                  <TD>
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-600 dark:bg-primary-500/15 dark:text-primary-400 shrink-0">
                        {member.fullName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-fg">{member.fullName}</p>
                        <p className="truncate text-xs text-fg-muted">{member.email}</p>
                      </div>
                    </div>
                  </TD>
                  <TD><RoleBadge role={member.role} /></TD>
                  <TD><StatusDot status={member.status} /></TD>
                  <TD className="text-[11px] text-fg-muted">
                    {member.createdAt ? new Date(member.createdAt).toLocaleDateString() : '—'}
                  </TD>
                  <TD>
                    <MemberActions member={member} onCredentials={setCredentials} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {/* Role matrix */}
      <RoleMatrix />

      {/* Modals */}
      {showInvite && (
        <InviteModal onClose={() => setShowInvite(false)} onSuccess={handleInviteSuccess} />
      )}
      {credentials && (
        <CredentialsModal result={credentials} onClose={() => setCredentials(null)} />
      )}
    </div>
  );
}
